import { message, Spin } from 'antd'
import React, { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  completeStandaloneFastlane,
  completeStandaloneRoutine,
  completeStandaloneScheduledTask,
  getTodayWorkTasks,
  getWeeklyWorkTasks,
  incompleteStandaloneFastlane,
  incompleteStandaloneRoutine,
  incompleteStandaloneScheduledTask,
  toggleRoutineItemTodoItem,
  toggleScheduledTaskTodoItem,
} from '../../../api/standalone.api'
import {
  standaloneScheduleViewUserId,
  trackerBoardRefetchTrigger,
} from '../../../store/standaloneSchedule'
import { userState } from '../../../store/user'
import '../StandaloneSchedulePage.scss'
// import SectionHeader from '../../../components/apos/SectionHeader'
import SectionHeader from '../../../components/apos/SectionHeader'
import TrackerBoardList from '../ui/TrackerBoardList'
import StandAloneAdminDashBoard from './StandAloneAdminDashBoard'
import StandaloneFastPopup from './StandaloneFastPopup'
import StandaloneRoutineItemPopup from './StandaloneRoutineItemPopup'
import StandaloneScheduledTaskPopup from './StandaloneScheduledTaskPopup'

const TrackerBoardSection = ({
  title,
  subtitle,
  buttonText,
  onOpenTaskPopup,
  setIsWatchdogPopupOpen,
  setSelectedWatchdogId,
  setIsWatchdogTaskPopupOpen,
  setSelectedTaskId,
  setSelectedTask,
  onRefetch,
}) => {
  const [user] = useRecoilState(userState)
  const viewUserId = useRecoilValue(standaloneScheduleViewUserId)
  const effectiveUserId = viewUserId ?? user?.id
  const refetchTrigger = useRecoilValue(trackerBoardRefetchTrigger)
  const [works, setWorks] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [weeklyWorks, setWeeklyWorks] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })
  const [isWeeklyLoading, setIsWeeklyLoading] = useState(false)
  const [isWeeklyInitialLoading, setIsWeeklyInitialLoading] = useState(true)

  // 팝업 상태
  const [selectedWork, setSelectedWork] = useState(null)
  const [isFastPopupOpen, setIsFastPopupOpen] = useState(false)
  const [isRoutinePopupOpen, setIsRoutinePopupOpen] = useState(false)
  const [isScheduledPopupOpen, setIsScheduledPopupOpen] = useState(false)

  const fetchTodayWorks = async ({ page = 0, append = false } = {}) => {
    if (!effectiveUserId) return
    if (!append) {
      setIsInitialLoading(true)
    }
    setIsLoading(true)
    try {
      const res = await getTodayWorkTasks({
        salusUserId: effectiveUserId,
        page,
        size: 10,
        sort: 'createdAt,desc',
      })
      if (res.status === 200) {
        setWorks((prev) => {
          const apiContent = res.data?.content || []
          const nextContent = append
            ? [...prev.content, ...apiContent]
            : apiContent
          return {
            content: nextContent,
            pageable: res.data?.pageable || { pageNumber: 0, pageSize: 10 },
            totalElements: res.data?.totalElements || 0,
          }
        })
      }
    } catch (error) {
      console.log('getTodayWorkTasks error::', error)
      message.error(
        'Today 업무 조회에 실패했습니다. 잠시 후 다시 시도해주세요.',
      )
    } finally {
      setIsLoading(false)
      if (!append) {
        setIsInitialLoading(false)
      }
    }
  }

  const fetchWeeklyWorks = async ({ page = 0, append = false } = {}) => {
    if (!effectiveUserId) return
    if (!append) {
      setIsWeeklyInitialLoading(true)
    }
    setIsWeeklyLoading(true)
    try {
      const res = await getWeeklyWorkTasks({
        salusUserId: effectiveUserId,
        page,
        size: 10,
        sort: 'createdAt,desc',
      })
      if (res.status === 200) {
        setWeeklyWorks((prev) => {
          const apiContent = res.data?.content || []
          const nextContent = append
            ? [...prev.content, ...apiContent]
            : apiContent
          return {
            content: nextContent,
            pageable: res.data?.pageable || { pageNumber: 0, pageSize: 10 },
            totalElements: res.data?.totalElements || 0,
          }
        })
        // console.log('getWeeklyWorkTasks::', res.data)
      }
    } catch (error) {
      console.log('getWeeklyWorkTasks error::', error)
      message.error(
        'Weekly 업무 조회에 실패했습니다. 잠시 후 다시 시도해주세요.',
      )
    } finally {
      setIsWeeklyLoading(false)
      if (!append) {
        setIsWeeklyInitialLoading(false)
      }
    }
  }

  // 초기 로드 (URL :id 또는 로그인 유저 기준)
  useEffect(() => {
    fetchTodayWorks()
    fetchWeeklyWorks()
  }, [effectiveUserId])

  // refetch trigger 변경 시 refetch
  useEffect(() => {
    if (refetchTrigger > 0) {
      fetchTodayWorks({ page: 0 })
      fetchWeeklyWorks({ page: 0 })
    }
  }, [refetchTrigger])

  const hasMoreToday = works.content.length < works.totalElements
  const hasMoreWeekly = weeklyWorks.content.length < weeklyWorks.totalElements

  const handleLoadMoreToday = () => {
    if (isLoading || !hasMoreToday) return
    const nextPage = works.pageable.pageNumber + 1
    fetchTodayWorks({ page: nextPage, append: true })
  }

  const handleLoadMoreWeekly = () => {
    if (isWeeklyLoading || !hasMoreWeekly) return
    const nextPage = weeklyWorks.pageable.pageNumber + 1
    fetchWeeklyWorks({ page: nextPage, append: true })
  }

  const updateWorkCompletionLocal = (workId, nextComplete) => {
    setWorks((prev) => ({
      ...prev,
      content: (prev.content || []).map((w) =>
        w.workItemId === workId
          ? {
              ...w,
              isComplete: nextComplete,
              isCompleted: nextComplete,
              completed: nextComplete,
            }
          : w,
      ),
    }))
    setWeeklyWorks((prev) => ({
      ...prev,
      content: (prev.content || []).map((w) =>
        w.workItemId === workId
          ? {
              ...w,
              isComplete: nextComplete,
              isCompleted: nextComplete,
              completed: nextComplete,
            }
          : w,
      ),
    }))
  }

  const handleCompleteWork = async (work) => {
    if (!effectiveUserId) return

    const isCompleted =
      work.isComplete ?? work.isCompleted ?? work.completed ?? false
    const nextComplete = !isCompleted

    try {
      if (work.workType === 'FAST') {
        if (nextComplete) {
          await completeStandaloneFastlane(work.workItemId, effectiveUserId)
          message.success('Fast 업무가 완료되었습니다.')
        } else {
          await incompleteStandaloneFastlane(work.workItemId, effectiveUserId)
          message.success('Fast 업무가 미완료로 변경되었습니다.')
        }
      } else if (work.workType === 'ROUTINE') {
        if (nextComplete) {
          const res = await completeStandaloneRoutine(
            work.workItemId,
            effectiveUserId,
          )
          console.log('completeStandaloneRoutine res::', res)
          message.success('Routine 업무가 완료되었습니다.')
        } else {
          await incompleteStandaloneRoutine(work.workItemId, effectiveUserId)
          message.success('Routine 업무가 미완료로 변경되었습니다.')
        }
      } else if (work.workType === 'SCHEDULED') {
        if (nextComplete) {
          await completeStandaloneScheduledTask(
            work.workItemId,
            effectiveUserId,
          )
          message.success('Scheduled 업무가 완료되었습니다.')
        } else {
          await incompleteStandaloneScheduledTask(
            work.workItemId,
            effectiveUserId,
          )
          message.success('Scheduled 업무가 미완료로 변경되었습니다.')
        }
      }

      updateWorkCompletionLocal(work.workItemId, nextComplete)
    } catch (error) {
      console.log('completeWork error::', error)
      message.error('업무 상태 변경이 실패했습니다. 잠시 후 다시 시도해주세요.')
    }
  }

  const handleToggleChecklistItem = async ({ todoItemId, workType }) => {
    if (!user?.id) return
    if (!todoItemId || !workType) return

    try {
      if (workType === 'ROUTINE') {
        await toggleRoutineItemTodoItem(todoItemId, user.id)
      } else if (workType === 'SCHEDULED') {
        await toggleScheduledTaskTodoItem(todoItemId, user.id)
      } else {
        return
      }

      const updateLocal = (prev) => ({
        ...prev,
        content: prev.content.map((work) => ({
          ...work,
          todoItems: work.todoItems?.map((item) =>
            item.id === todoItemId
              ? {
                  ...item,
                  // 백엔드에서는 checked / isChecked를 사용하므로 둘 다 토글
                  checked: !(item.checked ?? item.isChecked ?? false),
                  isChecked: !(item.checked ?? item.isChecked ?? false),
                }
              : item,
          ),
        })),
      })

      setWorks(updateLocal)
      setWeeklyWorks(updateLocal)
    } catch (error) {
      console.log('handleToggleChecklistItem error::', error)
      message.error('체크리스트 상태 변경에 실패했습니다.')
    }
  }

  const handleWorkClick = (work) => {
    setSelectedWork(work)
    if (work.workType === 'FAST') {
      setIsFastPopupOpen(true)
    } else if (work.workType === 'ROUTINE') {
      setIsRoutinePopupOpen(true)
    } else if (work.workType === 'SCHEDULED') {
      setIsScheduledPopupOpen(true)
    }
  }

  const handleClosePopups = () => {
    setSelectedWork(null)
    setIsFastPopupOpen(false)
    setIsRoutinePopupOpen(false)
    setIsScheduledPopupOpen(false)
  }

  const handlePopupSuccess = () => {
    fetchTodayWorks({ page: 0 })
    fetchWeeklyWorks({ page: 0 })
  }

  return (
    <section className="standalone-schedule-container">
      <SectionHeader
        title={title}
        subtitle={subtitle}
        buttonText={buttonText}
        onButtonClick={onOpenTaskPopup}
      />
      <StandAloneAdminDashBoard
        onWatchdogClick={(watchdog) => {
          setSelectedWatchdogId(watchdog.id)
          setIsWatchdogPopupOpen(true)
        }}
      />
      <div style={{ marginBottom: '12px' }} />
      <div className="tracker-board-sections-wrapper">
        <div className="tracker-board-section-column">
          <Spin spinning={isInitialLoading}>
            <TrackerBoardList
              titleChip="Today"
              works={works.content}
              isLoading={isLoading}
              hasMore={hasMoreToday}
              onLoadMore={handleLoadMoreToday}
              length={works.totalElements}
              onCompleteWork={handleCompleteWork}
              onToggleChecklistItem={handleToggleChecklistItem}
              onWorkClick={handleWorkClick}
              showWatchdogList={true}
              setIsWatchdogPopupOpen={setIsWatchdogPopupOpen}
              setSelectedWatchdogId={setSelectedWatchdogId}
              setIsWatchdogTaskPopupOpen={setIsWatchdogTaskPopupOpen}
              setSelectedTaskId={setSelectedTaskId}
              setSelectedTask={setSelectedTask}
              onRefetch={onRefetch}
            />
          </Spin>
        </div>
        <div className="tracker-board-section-column">
          <Spin spinning={isWeeklyInitialLoading}>
            <TrackerBoardList
              titleChip="Weekly"
              works={weeklyWorks.content}
              isLoading={isWeeklyLoading}
              hasMore={hasMoreWeekly}
              onLoadMore={handleLoadMoreWeekly}
              length={weeklyWorks.totalElements}
              onCompleteWork={handleCompleteWork}
              onToggleChecklistItem={handleToggleChecklistItem}
              onWorkClick={handleWorkClick}
            />
          </Spin>
        </div>
      </div>

      {/* Fast 업무 상세 팝업 */}
      <StandaloneFastPopup
        id={selectedWork?.workItemId}
        open={isFastPopupOpen}
        onClose={handleClosePopups}
        onSuccess={handlePopupSuccess}
      />

      {/* Routine 업무 상세 팝업 */}
      <StandaloneRoutineItemPopup
        id={selectedWork?.workItemId}
        open={isRoutinePopupOpen}
        onClose={handleClosePopups}
        onSuccess={handlePopupSuccess}
      />

      {/* Scheduled 업무 상세 팝업 */}
      <StandaloneScheduledTaskPopup
        id={selectedWork?.workItemId}
        open={isScheduledPopupOpen}
        onClose={handleClosePopups}
        onSuccess={handlePopupSuccess}
      />
    </section>
  )
}

export default TrackerBoardSection
